import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { ErrorState } from '@/components/shell';
import type {
  CalendarFilters as CalendarFiltersShape,
  JizdaResponse,
} from '@/api/types/Jizda';
import { Roles } from '@/constants/roles';
import { NEW_ORDER_ID } from '@/constants/orders';
import { Routes } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectUserRoles } from '@/store/userSlice';
import {
  loadWeek,
  selectCalendarFilters,
  selectCalendarItems,
  selectCalendarStatus,
  selectCalendarWeekStart,
  setCalendarFilters,
  setWeekStart,
} from '@/store/calendarSlice';
import {
  addWeeks,
  formatWeekRangeCs,
  getWeekDays,
  getWeekStart,
  todayIsoDate,
} from '@/utils/date';

import { OrderEditor } from '../OrderEditor';
import { OrdersExport } from '../OrdersExport';

import { CalendarFilters } from './CalendarFilters';
import { OrdersViewToggle } from './OrdersViewToggle';
import { WeekCalendar } from './WeekCalendar';
import { WeekCalendarMobile } from './WeekCalendarMobile';

// `?week=YYYY-MM-DD` query param — the deep-linkable Monday of the displayed
// week (ADR-0051). Absent → current week.
const WEEK_PARAM = 'week';

// Sentinel `orderId` opening the editor for a brand-new jízda (mirrors
// OrdersPage / OrderEditor's `isNew` check). Drives "Nová jízda" on the
// calendar — opens the modal OVER the calendar, no navigation (change-spec
// item 2).

// Weekly transport calendar shell (CHR-03 §6 / ADR-0051). Owns: the
// Seznam|Kalendář toggle, calendar filters, the `< Dnes >` week navigator with
// a `formatWeekRangeCs` label, and the desktop/mobile canvas switch. Week
// navigation rides the `?week=` query param so refresh / back-forward is
// stable; the slice is kept in sync from that param.
export const CalendarPage = (): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `md` split matches the overview (tablets stay on the desktop grid).
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const weekStart = useAppSelector(selectCalendarWeekStart);
  const filters = useAppSelector(selectCalendarFilters);
  const items = useAppSelector(selectCalendarItems);
  const status = useAppSelector(selectCalendarStatus);
  const myRoles = useAppSelector(selectUserRoles);
  const isBackoffice = myRoles.includes(Roles.BACKOFFICE);

  // Detail = `OrderEditor` modal rendered OVER the calendar (change-spec item
  // 1). Local state only — NO route change — so `?week=` and the calendarSlice
  // filters are untouched while the modal is open/saved/closed. The editor
  // already renders read-only for a viewer / non-assignee / deleted jízda.
  const [editorOrderId, setEditorOrderId] = useState<string | null>(null);

  // The `?week=` param is the source of truth for which week is shown.
  // Normalise to the week's Monday; absent / malformed → current week.
  const paramWeek = searchParams.get(WEEK_PARAM);
  const targetWeekStart = useMemo(
    () => getWeekStart(paramWeek ?? todayIsoDate()),
    [paramWeek],
  );

  // Sync the slice's weekStart from the URL whenever the param changes.
  useEffect(() => {
    if (targetWeekStart !== weekStart) {
      dispatch(setWeekStart(targetWeekStart));
    }
  }, [dispatch, targetWeekStart, weekStart]);

  // (Re)load whenever the displayed week or the filters change.
  useEffect(() => {
    void dispatch(loadWeek());
  }, [dispatch, weekStart, filters]);

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const rangeLabel = useMemo(() => formatWeekRangeCs(weekStart), [weekStart]);

  // Write a Monday into the `?week=` param (drives the sync effect above).
  const goToWeek = useCallback(
    (mondayIso: string) => {
      navigate(`${Routes.ORDERS_CALENDAR}?${WEEK_PARAM}=${mondayIso}`);
    },
    [navigate],
  );

  const handlePrev = useCallback(
    () => goToWeek(addWeeks(weekStart, -1)),
    [goToWeek, weekStart],
  );
  const handleNext = useCallback(
    () => goToWeek(addWeeks(weekStart, 1)),
    [goToWeek, weekStart],
  );
  const handleToday = useCallback(
    () => goToWeek(getWeekStart(todayIsoDate())),
    [goToWeek],
  );

  const handleFiltersChange = useCallback(
    (next: Partial<CalendarFiltersShape>) => {
      dispatch(setCalendarFilters(next));
    },
    [dispatch],
  );

  // Click → open the jízda's existing detail as a modal OVER the calendar
  // (change-spec item 1). No navigation: the calendar (week + filters) stays
  // mounted underneath and is shown unchanged on close.
  const handleOpen = useCallback((id: string) => setEditorOrderId(id), []);
  const handleCloseEditor = useCallback(() => setEditorOrderId(null), []);

  // A save/delete from the modal refreshes the displayed week so the edited
  // fůra reflects its new state without leaving the calendar.
  const handleEditorChanged = useCallback(
    (_changed?: JizdaResponse | string) => {
      void dispatch(loadWeek());
    },
    [dispatch],
  );

  const isEmpty = status === 'ready' && items.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: isCompact ? undefined : 'hidden',
      }}
    >
      {/* Dedicated toolbar row mirroring the OrdersPage layout (change-spec
          item 2 — reverts the prior hide): Seznam|Kalendář switch LEFT, the
          backoffice write/export actions (Export + Nová jízda) RIGHT, with the
          SAME role gating the list page uses. "Nová jízda" opens the editor
          OVER the calendar (`NEW_ORDER_ID`) — no navigation; a save refreshes
          the week via `handleEditorChanged`. The calendar's DATA view stays
          read-only; only the global create/export affordances return. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: theme.spacing(2), flexWrap: 'wrap', gap: theme.spacing(1.5) }}
      >
        <OrdersViewToggle value="calendar" />
        {isBackoffice ? (
          <Stack direction="row" gap={theme.spacing(1.5)} alignItems="center">
            <OrdersExport />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEditorOrderId(NEW_ORDER_ID)}
              data-testid="new-order-button"
            >
              {t('newOrder')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      <CalendarFilters filters={filters} onChange={handleFiltersChange} />
      {/* Week navigator: `< Dnes >` + range label. */}
      <Stack
        direction="row"
        alignItems="center"
        gap={theme.spacing(1)}
        sx={{ pb: theme.spacing(2) }}
      >
        <IconButton
          onClick={handlePrev}
          aria-label={t('calendar.prevWeek')}
          data-testid="calendar-prev-week"
          sx={{ width: 44, height: 44 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Button
          variant="outlined"
          size="small"
          onClick={handleToday}
          data-testid="calendar-today"
        >
          {t('calendar.today')}
        </Button>
        <IconButton
          onClick={handleNext}
          aria-label={t('calendar.nextWeek')}
          data-testid="calendar-next-week"
          sx={{ width: 44, height: 44 }}
        >
          <ChevronRightIcon />
        </IconButton>
        <Typography
          component="span"
          sx={{ fontSize: 14, fontWeight: 600, ml: theme.spacing(0.5) }}
          data-testid="calendar-week-range"
        >
          {rangeLabel}
        </Typography>
      </Stack>
      {status === 'error' ? (
        <ErrorState
          icon={<ErrorOutlineIcon />}
          title={t('calendar.loadFailed')}
          description={t('calendar.subtitle')}
          data-testid="calendar-error"
          actions={
            <Button variant="outlined" onClick={() => void dispatch(loadWeek())}>
              {t('calendar.retry')}
            </Button>
          }
        />
      ) : (
        // Empty week KEEPS the grid (change-spec item 4): the empty Po–Ne grid
        // (desktop) / day-stack (mobile) still renders — each column empty —
        // with the "no transports" message as a subtle banner ABOVE it, never
        // replacing the grid.
        <Box
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          {isEmpty ? (
            <Alert
              severity="info"
              variant="outlined"
              sx={{ mb: theme.spacing(1.5), py: theme.spacing(0.25) }}
              data-testid="calendar-empty-week"
            >
              {t('calendar.emptyWeek')}
            </Alert>
          ) : null}
          {isCompact ? (
            <WeekCalendarMobile weekDays={weekDays} items={items} onOpen={handleOpen} />
          ) : (
            <WeekCalendar weekDays={weekDays} items={items} onOpen={handleOpen} />
          )}
        </Box>
      )}
      {/* Detail modal overlaid on the calendar — no route change (item 1). */}
      <OrderEditor
        orderId={editorOrderId}
        onClose={handleCloseEditor}
        onSaved={handleEditorChanged}
        onDeleted={handleEditorChanged}
      />
    </Box>
  );
};
